import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../../service/Api";
import { Loader2, Package, Users, FileText, Plus, Palette } from "lucide-react";
import ProductsSidePanel from "../../components/tailspin/ProductsSidePanel";
import ProspectsSidePanel from "../../components/tailspin/ProspectsSidePanel";

export default function TailspinDashboard() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [collaterals, setCollaterals] = useState([]);
  const [prospectsTotal, setProspectsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isProductsPanelOpen, setIsProductsPanelOpen] = useState(false);
  const [isProspectsPanelOpen, setIsProspectsPanelOpen] = useState(false);

  // Pagination and search states for collaterals
  const [collateralsPage, setCollateralsPage] = useState(1);
  const [collateralsPageSize] = useState(6);
  const [collateralsTotal, setCollateralsTotal] = useState(0);
  const [collateralsTotalPages, setCollateralsTotalPages] = useState(0);
  const [collateralsSearch, setCollateralsSearch] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Reload collaterals when page or search changes
  useEffect(() => {
    if (!loading) {
      loadCollaterals();
    }
  }, [collateralsPage, collateralsSearch]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [productsRes, prospectsRes] = await Promise.all([
        apiService.getTailspinProducts(),
        apiService.getTailspinProspects()
      ]);

      console.log("Dashboard API Responses:", {
        products: productsRes.data,
        prospects: prospectsRes.data
      });

      if (productsRes.data.success) {
        setProducts(productsRes.data.data || []);
      }
      if (prospectsRes.data.success) {
        const prospectData = prospectsRes.data.data || {};
        const items = Array.isArray(prospectData) ? prospectData : prospectData.items || [];
        const total =
          typeof prospectData.total === "number"
            ? prospectData.total
            : items.length;
        setProspects(items);
        setProspectsTotal(total);
      }

      // Load collaterals separately with pagination
      await loadCollaterals();
    } catch (err) {
      console.error("Error loading dashboard data:", err);
      console.error("Error details:", err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCollaterals = async () => {
    try {
      const collateralsRes = await apiService.getTailspinCollaterals({
        page: collateralsPage,
        pageSize: collateralsPageSize,
        search: collateralsSearch
      });

      console.log("Collaterals API Response:", collateralsRes.data);

      if (collateralsRes.data.success) {
        setCollaterals(collateralsRes.data.data || []);
        if (collateralsRes.data.pagination) {
          setCollateralsTotal(collateralsRes.data.pagination.total);
          setCollateralsTotalPages(collateralsRes.data.pagination.totalPages);
        }
      }
    } catch (err) {
      console.error("Error loading collaterals:", err);
      console.error("Error details:", err.response?.data || err.message);
    }
  };

  // Optimized function to only update products count without full reload
  const updateProductsCount = useCallback(async () => {
    try {
      const productsRes = await apiService.getTailspinProducts();
      if (productsRes.data.success) {
        setProducts(productsRes.data.data || []);
      }
    } catch (error) {
      console.error("Error updating products count:", error);
    }
  }, []);

  // Optimized function to only update prospects count without full reload
  const updateProspectsCount = useCallback(async () => {
    try {
      const prospectsRes = await apiService.getTailspinProspects();
      if (prospectsRes.data.success) {
        const prospectData = prospectsRes.data.data || {};
        const items = Array.isArray(prospectData) ? prospectData : prospectData.items || [];
        const total = typeof prospectData.total === "number" ? prospectData.total : items.length;
        setProspects(items);
        setProspectsTotal(total);
      }
    } catch (error) {
      console.error("Error updating prospects count:", error);
    }
  }, []);


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: '#FF6B5A' }} />
          <p style={{ color: '#0A0A10' }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#0A0A10' }}>
            Tailspin Dashboard
          </h1>
          <p className="text-xl" style={{ color: '#0A0A10', opacity: 0.7 }}>
            Manage your products, prospects, and collaterals
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div 
            className="rounded-xl shadow-sm border p-6 cursor-pointer transition-all"
            style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.transform = 'translateY(0)'; }}
            onClick={() => setIsProductsPanelOpen(true)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 107, 90, 0.15)' }}>
                <Package className="w-6 h-6" style={{ color: '#FF6B5A' }} />
              </div>
              <span className="text-3xl font-bold" style={{ color: '#0A0A10' }}>{products.length}</span>
            </div>
            <h3 className="text-lg font-semibold mb-1" style={{ color: '#0A0A10' }}>Products</h3>
            <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>Active products in your catalog</p>
          </div>

          <div 
            className="rounded-xl shadow-sm border p-6 cursor-pointer transition-all"
            style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.transform = 'translateY(0)'; }}
            onClick={() => setIsProspectsPanelOpen(true)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(215, 180, 114, 0.15)' }}>
                <Users className="w-6 h-6" style={{ color: '#D7B472' }} />
              </div>
              <span className="text-3xl font-bold" style={{ color: '#0A0A10' }}>{prospectsTotal}</span>
            </div>
            <h3 className="text-lg font-semibold mb-1" style={{ color: '#0A0A10' }}>Prospects</h3>
            <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>Target companies to engage</p>
          </div>

          <div className="rounded-xl shadow-sm border p-6" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 107, 90, 0.15)' }}>
                <FileText className="w-6 h-6" style={{ color: '#FF6B5A' }} />
              </div>
              <span className="text-3xl font-bold" style={{ color: '#0A0A10' }}>{collateralsTotal}</span>
            </div>
            <h3 className="text-lg font-semibold mb-1" style={{ color: '#0A0A10' }}>Collaterals</h3>
            <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>Generated sales materials</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="rounded-xl shadow-sm border p-8 mb-8" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
          <h2 className="text-2xl font-semibold mb-6" style={{ color: '#0A0A10' }}>Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          
          

            <button
              onClick={() => navigate("/tailspin/collateral")}
              className="flex items-center gap-3 p-4 border-2 border-dashed rounded-lg transition-colors text-left"
              style={{ borderColor: '#D7B472', backgroundColor: 'transparent' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.backgroundColor = 'rgba(255, 107, 90, 0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(255, 107, 90, 0.15)' }}>
                <Plus className="w-5 h-5" style={{ color: '#FF6B5A' }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: '#0A0A10' }}>Create Collateral</h3>
                <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>Generate sales material</p>
              </div>
            </button>

            <button
              onClick={() => navigate("/tailspin/brand-assets")}
              className="flex items-center gap-3 p-4 border-2 border-dashed rounded-lg transition-colors text-left"
              style={{ borderColor: '#D7B472', backgroundColor: 'transparent' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.backgroundColor = 'rgba(215, 180, 114, 0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(215, 180, 114, 0.15)' }}>
                <Palette className="w-5 h-5" style={{ color: '#D7B472' }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: '#0A0A10' }}>Brand Assets</h3>
                <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>Manage brand identity</p>
              </div>
            </button>
          </div>
        </div>

        {/* Recent Products */}
        {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Recent Products</h2>
          {products.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No products yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.slice(0, 6).map((product) => (
                <div
                  key={product.id}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors cursor-pointer"
                  onClick={() => navigate(`/tailspin/products/${product.id}`)}
                >
                  <h3 className="font-semibold text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{product.description}</p>
                  {product.domain && (
                    <p className="text-xs text-blue-600 mt-2">{product.domain}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div> */}

        {/* Recent Prospects */}
        {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Recent Prospects</h2>
          {prospects.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No prospects yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {prospects.slice(0, 6).map((prospect) => (
                <div
                  key={prospect.id}
                  className="p-4 border border-gray-200 rounded-lg hover:border-green-300 transition-colors cursor-pointer"
                  onClick={() => navigate(`/tailspin/prospects/${prospect.id}`)}
                >
                  <h3 className="font-semibold text-gray-900 mb-2">{prospect.name}</h3>
                  {prospect.domain && (
                    <p className="text-sm text-gray-600">{prospect.domain}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div> */}

        {/* Recent Collaterals */}
        <div className="rounded-xl shadow-sm border p-8 mt-8" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold" style={{ color: '#0A0A10' }}>Recent Collaterals</h2>
            <div className="flex items-center gap-4">
              <input
                type="text"
                placeholder="Search by title..."
                value={collateralsSearch}
                onChange={(e) => {
                  setCollateralsSearch(e.target.value);
                  setCollateralsPage(1); // Reset to first page on search
                }}
                className="px-4 py-2 rounded-lg border outline-none"
                style={{ borderColor: '#D7B472' }}
              />
              {collateralsSearch && (
                <button
                  onClick={() => {
                    setCollateralsSearch('');
                    setCollateralsPage(1);
                  }}
                  className="px-4 py-2 rounded-lg transition-colors"
                  style={{ backgroundColor: '#FF6B5A', color: 'white' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e55a4a'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          {collaterals.length === 0 ? (
            <div className="text-center py-8" style={{ color: '#0A0A10', opacity: 0.7 }}>
              <FileText className="w-12 h-12 mx-auto mb-3" style={{ color: '#0A0A10', opacity: 0.5 }} />
              <p>{collateralsSearch ? 'No collaterals found matching your search' : 'No collaterals yet'}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {collaterals.map((collateral) => {
                const title =
                  collateral.title ||
                  collateral.collateral?.collateral?.title ||
                  collateral.collateral?.title ||
                  "Untitled Collateral";
                const description =
                  collateral.description ||
                  collateral.collateral?.description ||
                  collateral.collateral?.collateral?.description ||
                  "";
                const createdAt = collateral.created_at
                  ? new Date(collateral.created_at).toLocaleString()
                  : null;

                return (
                  <div
                    key={collateral.id}
                    className="p-4 border rounded-lg transition-colors cursor-pointer"
                    style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                    onClick={() => collateral.slug && navigate(`/tailspin/collateral/${collateral.slug}`)}
                  >
                    <h3 className="font-semibold mb-2 truncate" style={{ color: '#0A0A10' }}>{title}</h3>
                    {description && (
                      <p className="text-sm line-clamp-2 mb-3" style={{ color: '#0A0A10', opacity: 0.7 }}>
                        {description}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-xs" style={{ color: '#0A0A10', opacity: 0.6 }}>
                      <span>Product #{collateral.product_id}</span>
                      {createdAt && <span>{createdAt}</span>}
                    </div>
                  </div>
                );
              })}
              </div>

              {/* Pagination Controls */}
              {collateralsTotalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>
                    Showing {((collateralsPage - 1) * collateralsPageSize) + 1} to {Math.min(collateralsPage * collateralsPageSize, collateralsTotal)} of {collateralsTotal} collaterals
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCollateralsPage(prev => Math.max(1, prev - 1))}
                      disabled={collateralsPage === 1}
                      className="px-4 py-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ 
                        backgroundColor: collateralsPage === 1 ? '#D7B472' : '#FF6B5A',
                        color: 'white' 
                      }}
                      onMouseEnter={(e) => { 
                        if (collateralsPage !== 1) e.currentTarget.style.backgroundColor = '#e55a4a'; 
                      }}
                      onMouseLeave={(e) => { 
                        if (collateralsPage !== 1) e.currentTarget.style.backgroundColor = '#FF6B5A'; 
                      }}
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2" style={{ color: '#0A0A10' }}>
                      Page {collateralsPage} of {collateralsTotalPages}
                    </span>
                    <button
                      onClick={() => setCollateralsPage(prev => Math.min(collateralsTotalPages, prev + 1))}
                      disabled={collateralsPage === collateralsTotalPages}
                      className="px-4 py-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ 
                        backgroundColor: collateralsPage === collateralsTotalPages ? '#D7B472' : '#FF6B5A',
                        color: 'white' 
                      }}
                      onMouseEnter={(e) => { 
                        if (collateralsPage !== collateralsTotalPages) e.currentTarget.style.backgroundColor = '#e55a4a'; 
                      }}
                      onMouseLeave={(e) => { 
                        if (collateralsPage !== collateralsTotalPages) e.currentTarget.style.backgroundColor = '#FF6B5A'; 
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Products Side Panel */}
      <ProductsSidePanel
        isOpen={isProductsPanelOpen}
        onClose={() => setIsProductsPanelOpen(false)}
        onProductsUpdate={updateProductsCount}
      />

      {/* Prospects Side Panel */}
      <ProspectsSidePanel
        isOpen={isProspectsPanelOpen}
        onClose={() => setIsProspectsPanelOpen(false)}
        onProspectsUpdate={updateProspectsCount}
      />
    </div>
  );
}

